import React, { useEffect, useState } from 'react';

import { storeNationalId } from '@tb-core/helpers/next-env';
import sessionStorage from '@tb-core/helpers/session-storage';
import { getQueryStoreId, isValidStoreId } from '@tb-core/helpers/store';
import useUserContext from '@tb-core/hooks/use-user-context';
import getStore from '@tb-core/providers/get-store';
import { JsxChildren, Store } from '@tb-core/types';

interface StoreProviderProps {
    children: JsxChildren;
    initialStore?: Store;
}
const StoreContext = React.createContext<Store | undefined>(undefined);
const { Consumer, Provider } = StoreContext;

const StoreProvider = ({ children, initialStore }: StoreProviderProps) => {
    const { storeId } = useUserContext();
    const [storeData, setStoreData] = useState<Store>(
        initialStore?.storeId
            ? initialStore
            : { storeId: storeId || storeNationalId }
    );
    const fetchStore = async () => {
        const updateStore = (store: Store) => {
            // Update store data in Session Storage.
            sessionStorage.setItems({ store });
            setStoreData(store);
        };
        const { store } = sessionStorage.getItems() as { store: Store };
        const sessionStore = store;
        const queryStoreId = getQueryStoreId(window.location.search);

        // Use session store (e.g. client not SSR) if it exists and
        // the session store id is the same as user store id and
        // there is no querystring
        if (sessionStore && sessionStore.storeId === storeId && !queryStoreId) {
            setStoreData(sessionStore);
        } else {
            // Rules of precedence from highest to lowest:
            // 1. query store id
            // 2. session user store id which is also Hybris session currentStore
            // 3. session store's store id
            // 4. last resort storeData store id if not national
            const currentStoreId =
                queryStoreId ||
                storeId ||
                sessionStore?.storeId ||
                storeData?.storeId;

            // National store information is not needed so skip the fetch
            if (
                currentStoreId &&
                currentStoreId !== storeNationalId &&
                isValidStoreId(currentStoreId)
            ) {
                updateStore(await getStore(currentStoreId));
            }
        }
    };
    // "Store ID update" effect
    useEffect(() => {
        fetchStore();
    }, [storeId]);

    return <Provider value={storeData}>{children}</Provider>;
};

const StoreConsumer = Consumer;

export default StoreProvider;
export { StoreConsumer, StoreContext };
